import { IAnalyticsService } from 'angulartics';
import { AttachmentService } from '@go-modules/services/api/attachments/attachments.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Attachment } from 'go-modules/services/attachment/attachment';

export interface Bindings {
	session: any;
	user: any;
	group: any;
	readonlyMode: boolean;
	analyticsCategory: string;
	analyticsLabel: string;
	activityName: string;
}

export class SessionAttachmentsController implements Bindings {

	// Bindings
	public session: any;
	public user: any;
	public group: any;
	public readonlyMode: boolean;
	public analyticsCategory: string;
	public analyticsLabel: string;
	public activityName: string;
	public options: any;

	public attachmentOptions: any;
	public attachments: Attachment[];
	public areAttachmentsLoading = false;
	public analyticsData: {category: string; label: string};
	public destroy$ = new Subject<void>();

	/* @ngInject */
	constructor (
		private $analytics: IAnalyticsService,
		private Session: any,
		private ngxAttachmentService: AttachmentService
	) {
		this.attachments = [];
	}

	public $onDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
	/**
	 * Handles init life-cycle hook
	 */
	public $onInit (): void {
		this.attachmentOptions = {
			groupId: this.session.group_id,
			hideTitle: true,
			hideDuration: true,
			hideLoading: (this.options && this.options.hideLoading) ? true : false,
			showAdd: !this.readonlyMode,
			showDelete: (attachment: any) => {
				if (this.readonlyMode) {
					return false;
				}
				return this.mayEditOrDeleteAttachment(attachment);
			},
			showEdit: (attachment: any) => {
				if (this.readonlyMode) {
					return false;
				}
				return this.mayEditOrDeleteAttachment(attachment);
			},
			showPreview: () => !this.readonlyMode,
			resourceId: this.session.session_id,
			resourceType: this.Session.RESOURCE_TYPE,
			userFirstName: this.user.first_name,
			userLastName: this.user.last_name,
			activityName: this.activityName
		};

		// Load session attachments
		if (this.session.num_attachments > 0) {
			this.areAttachmentsLoading = true;
			this.ngxAttachmentService.getSessionAttachments(this.session.session_id).pipe(
				takeUntil(this.destroy$),
				finalize(()=> this.areAttachmentsLoading = false)
			).subscribe((attachments)=> this.attachments = attachments);
		}

		this.analyticsData = {
			category: this.analyticsCategory,
			label: this.analyticsLabel
		};
	}

	/**
	 * Whether an attachment may be edited or deleted
	 */
	public mayEditOrDeleteAttachment (attachment: any): boolean {
		return attachment.created_by === this.user.user_id
			|| this.group.hasInstructorRole(true)
			|| this.session.isOwner(this.user);
	}

	/**
	 * Handles attachment added event
	 */
	public onAttachmentAdded (attachments: any[]): void {
		this.session.num_attachments = attachments.length;

		this.$analytics.eventTrack('add attachment', this.analyticsData);
	}

	/**
	 * Handles attachment preview event
	 */
	public onAttachmentPreview (): void {
		this.$analytics.eventTrack('preview attachment', this.analyticsData);
	}

	/**
	 * Handles attachment download event
	 */
	public onAttachmentDownload (): void {
		this.$analytics.eventTrack('download attachment', this.analyticsData);
	}

	/**
	 * Handles attachment edit event
	 */
	public onAttachmentEdit (): void {
		this.$analytics.eventTrack('edit attachment', this.analyticsData);
	}

	/**
	 * Handles attachment deleted event
	 */
	public onAttachmentDeleted (attachments: any[]): void {
		this.session.num_attachments = attachments.length;

		this.$analytics.eventTrack('delete attachment', this.analyticsData);
	}
}
